/**
 * Convert a recorded audio Blob (webm/mp4/ogg) to mono 16-bit PCM WAV for Gemini STT.
 * Fragmented MediaRecorder webm often fails cloud decode; WAV is reliably accepted.
 */
export async function blobToWav(blob) {
  if (!blob || blob.size < 64) {
    throw new Error("empty_audio");
  }
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) {
    throw new Error("no_audio_context");
  }
  const ctx = new AudioCtx();
  try {
    const ab = await blob.arrayBuffer();
    const decoded = await ctx.decodeAudioData(ab.slice(0));
    const offline = new OfflineAudioContext(
      1,
      decoded.length,
      decoded.sampleRate,
    );
    const source = offline.createBufferSource();
    // Mixdown to mono
    const mono = offline.createBuffer(1, decoded.length, decoded.sampleRate);
    const out = mono.getChannelData(0);
    const channels = decoded.numberOfChannels;
    for (let i = 0; i < decoded.length; i += 1) {
      let sum = 0;
      for (let c = 0; c < channels; c += 1) {
        sum += decoded.getChannelData(c)[i];
      }
      out[i] = sum / channels;
    }
    source.buffer = mono;
    source.connect(offline.destination);
    source.start(0);
    const rendered = await offline.startRendering();
    return encodeWav(rendered.getChannelData(0), rendered.sampleRate);
  } finally {
    try { await ctx.close(); } catch (_) { /* noop */ }
  }
}

function encodeWav(samples, sampleRate) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i += 1, offset += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([buffer], { type: "audio/wav" });
}

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i += 1) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}
